import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { ComplianceFooter, ComplianceCallout } from "@/components/Compliance";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Check, BookOpen, ListChecks, HelpCircle, ArrowRight, ShieldCheck } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1586880244406-556ebe35f282?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState({ course: null, modules: [] });
  const [price, setPrice] = useState(null);
  const [checkout, setCheckout] = useState("checkout");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/course").then((r) => setData(r.data)).catch(() => {});
    api.get("/payments/price").then((r) => setPrice(r.data)).catch(() => {});
    api.get("/compliance", { params: { location: "checkout" } }).then((r) => setCheckout(r.data?.[0] || null)).catch(() => {});
  }, []);

  const buy = async () => {
    if (!user) {
      navigate("/register");
      return;
    }
    if (user.has_access || user.role === "admin") {
      navigate("/dashboard");
      return;
    }
    setLoading(true);
    try {
      const { data: res } = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  const stages = ["Foundations", "Find and Approve", "Build and Launch", "Run and Grow"];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-12 pb-16 grid lg:grid-cols-2 gap-10 items-center">
        <div className="animate-fade-up">
          <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-100 border-0 mb-5">Edition 1 · October 2026</Badge>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-slate-900">
            {data.course?.title || "The High-Ticket Store Blueprint 2026"}
          </h1>
          <p className="mt-4 text-lg text-slate-600 max-w-xl">
            {data.course?.subtitle || "The Honest Step-by-Step Dropshipping Course"}
          </p>
          <p className="mt-4 text-base text-slate-600 max-w-xl leading-relaxed">
            {data.course?.description}
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-4">
            <Button
              size="lg"
              className="bg-slate-900 hover:bg-slate-800 h-12 px-7 text-base"
              onClick={buy}
              disabled={loading}
              data-testid="landing-buy-button"
            >
              {loading ? "Starting…" : user?.has_access || user?.role === "admin" ? "Go to dashboard" : `Get full access${price?.display ? ` · ${price.display}` : ""}`}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <ShieldCheck className="w-4 h-4 text-emerald-600" /> One-time payment · 15 modules
            </div>
          </div>
          <div className="mt-6">
            <ComplianceCallout block={checkout} testId="checkout-compliance-disclaimer" />
          </div>
        </div>
        <div className="relative animate-fade-up">
          <div className="absolute -inset-4 bg-gradient-to-tr from-amber-200/40 to-transparent rounded-3xl blur-2xl" />
          <img
            src={HERO_IMG}
            alt="High-ticket ecommerce workspace"
            className="relative rounded-2xl border border-slate-200 shadow-xl w-full object-cover aspect-[4/3]"
          />
        </div>
      </section>

      {/* Value props */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid sm:grid-cols-3 gap-4">
        {[
          { icon: BookOpen, t: "Guided lessons", d: "Work through readings, case studies and worksheets at your pace." },
          { icon: ListChecks, t: "Checklists & progress", d: "Track every lesson, task and checklist with honest skip warnings." },
          { icon: HelpCircle, t: "Fixed quizzes", d: "Short, admin-reviewed quizzes at the end of each module." },
        ].map((f, i) => (
          <div key={i} className="rounded-xl border border-slate-200 bg-white p-5">
            <f.icon className="w-6 h-6 text-slate-900 mb-3" />
            <h3 className="font-display font-semibold text-slate-900">{f.t}</h3>
            <p className="text-sm text-slate-600 mt-1">{f.d}</p>
          </div>
        ))}
      </section>

      {/* Curriculum */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">The 15-module curriculum</h2>
        <p className="text-slate-600 mt-2">From foundations to a 30-day launch plan — USA first, with UK notes.</p>
        <div className="mt-8 space-y-10">
          {stages.map((stage) => {
            const mods = data.modules.filter((m) => m.stage === stage);
            if (!mods.length) return null;
            return (
              <div key={stage}>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-700 mb-3">{stage}</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {mods.map((m) => (
                    <div key={m.id} className="rounded-xl border border-slate-200 bg-white p-5 hover:border-slate-300 transition-colors" data-testid={`landing-module-${m.module_number}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-mono text-xs font-semibold text-slate-400">{String(m.module_number).padStart(2, "0")}</span>
                      </div>
                      <h3 className="font-display font-semibold text-slate-900 leading-snug">{m.title}</h3>
                      <p className="text-sm text-slate-600 mt-2 line-clamp-3">{m.summary}</p>
                      <div className="mt-3 flex items-center gap-3 text-xs text-slate-400">
                        <span>{m.lesson_count} lessons</span>
                        <span>·</span>
                        <span>{m.checklist_count} checklist items</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-10 text-center">
          <Button size="lg" className="bg-slate-900 hover:bg-slate-800" onClick={buy} disabled={loading} data-testid="landing-buy-button-bottom">
            {user?.has_access || user?.role === "admin" ? "Go to dashboard" : `Get full access${price?.display ? ` · ${price.display}` : ""}`}
          </Button>
        </div>
      </section>

      <ComplianceFooter />
    </div>
  );
}
